import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import ReactDOM from "react-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Search, TrendingUpDown, CircleOff, Plus, Check, ChevronDown, Percent, RotateCcw } from "lucide-react";
import TabSelector from "../ui/TabSelector";
import Tab from "../ui/Tab";
import Label from "../Label";
import Input from "../Input";
import Chip from "../ui/Chip";
import Divider from "../ui/Divider";
import Button from "../Buttons/Button";
import TaplrTooltip from "../Tooltip";
import { Tooltip } from "@material-ui/core";

const ProjectionsDialog = ({ dropdownOpen, accountCategories, priceBundles, currency, handleSetActiveProjection, activeProjection, mobileAlignment, ...props }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const buttonRef = useRef(null);

  // Tabs
  const [selectedTab, setSelectedTab] = useState("priceDefinitions");

  // Account categories
  const [allAccounts, setAllAccounts] = useState([]);
  const [accountSearchValue, setAccountSearchValue] = useState("");
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);

  // account dropdown logic
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const dropdownRef = useRef(null);
  const openDropdownButtonRef = useRef(null);

  const [currentProjection, setCurrentProjection] = useState(null);
  const [appliedProjection, setAppliedProjection] = useState(null); // The projection that is applied to the parent page content
  const [revenueCollapsed, setRevenueCollapsed] = useState(false);
  const [costsCollapsed, setCostsCollapsed] = useState(false);

  // Price definitions
  const [allPriceDefinitions, setAllPriceDefinitions] = useState([]);
  const [priceDefinitionSearchValue, setPriceDefinitionSearchValue] = useState("");
  const [priceDefinitionDropdownOpen, setPriceDefinitionDropdownOpen] = useState(false);
  const [pdDropdownPosition, setPdDropdownPosition] = useState({ top: 0, left: 0 });
  const [pdDropdownWidth, setPdDropdownWidth] = useState(0);
  const pdDropdownRef = useRef(null);
  const openPdDropdownButtonRef = useRef(null);

  // create mobileAlignment class mobileAlignment_"prop"

  const mobileAlignmentClass = clsx({
    [classes[`${mobileAlignment}MobileAlignment`]]: mobileAlignment !== null,
  });

  useEffect(() => {
    let tempAccounts = null;
    if (accountCategories && accountCategories.length > 0) {
      // for every account in accountCategory.accounts, set isRevenue (get from category) and push to tempAccounts
      tempAccounts = accountCategories.map((category) => {
        return category.accounts.map((account) => {
          return {
            ...account,
            categoryName: category.name,
            categoryId: category.id,
            isRevenue: category.isRevenue,
            companyName: category.companyName,
          };
        });
      });
      // flatten the array
      tempAccounts = tempAccounts.flat();
      setAllAccounts(tempAccounts);
    } else {
      // if no account categories, set allAccounts to empty array
      setAllAccounts([]);
    }
  }, [accountCategories]);

  // Flatten priceBundles into selectable price definitions
  useEffect(() => {
    if (priceBundles && priceBundles.length > 0) {
      let tempPriceDefinitions = [];
      priceBundles.forEach((company) => {
        company.priceBundles.forEach((bundle) => {
          bundle.items.forEach((item) => {
            // Deduplicate by priceDefinitionId only
            if (!tempPriceDefinitions.some((pd) => pd.priceDefinitionId === item.priceDefinitionId)) {
              tempPriceDefinitions.push({
                priceDefinitionId: item.priceDefinitionId,
                priceDefinitionName: item.priceDefinitionName,
                price: item.price,
              });
            }
          });
        });
      });
      setAllPriceDefinitions(tempPriceDefinitions);
    } else {
      setAllPriceDefinitions([]);
    }
  }, [priceBundles]);

  // PriceDefinition Dropdown positioning
  useEffect(() => {
    if (priceDefinitionDropdownOpen && openPdDropdownButtonRef.current) {
      const rect = openPdDropdownButtonRef.current.getBoundingClientRect();
      setPdDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
      setPdDropdownWidth(rect.width);
    }
  }, [priceDefinitionDropdownOpen]);

  useEffect(() => {
    const handleClickOutsidePdDropdown = (event) => {
      if (pdDropdownRef.current && !pdDropdownRef.current.contains(event.target) && priceDefinitionDropdownOpen) {
        if (!openPdDropdownButtonRef.current.contains(event.target)) {
          setPriceDefinitionDropdownOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutsidePdDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsidePdDropdown);
    };
  }, [priceDefinitionDropdownOpen]);

  // AccountDropdown Logic
  useEffect(() => {
    if (accountDropdownOpen && openDropdownButtonRef.current) {
      const rect = openDropdownButtonRef.current.getBoundingClientRect();

      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // Adjust for scroll position
        left: rect.left + window.scrollX, // Adjust for horizontal scrolling
      });

      setDropdownWidth(rect.width);
    }
  }, [accountDropdownOpen]);

  useEffect(() => {
    const handleClickOutsideAccountDropdown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && accountDropdownOpen) {
        // check if target is not the button
        if (!openDropdownButtonRef.current.contains(event.target)) {
          setAccountDropdownOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutsideAccountDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideAccountDropdown);
    };
  }, [accountDropdownOpen]);

  // Dropdown logic
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (
      buttonRef.current &&
      !buttonRef.current.contains(event.target) &&
      (!dropdownRef.current || !dropdownRef.current.contains(event.target)) &&
      (!pdDropdownRef.current || !pdDropdownRef.current.contains(event.target))
    ) {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Tab selection logic
  const handleGraphTypeSelection = (value) => {
    setSelectedTab(value);
  };

  const handleAccountSelection = (account) => {
    let tempAccount = {
      percentage: 100,
      ...account,
    };
    let tempProjection = null;
    if (currentProjection) {
      tempProjection = { ...currentProjection };
    } else {
      tempProjection = { accounts: [] };
    }

    if (tempProjection.accounts.some((a) => a.id === tempAccount.id)) {
      // if account is already selected, remove it from the projection
      tempProjection.accounts = tempProjection.accounts.filter((acc) => acc.id !== tempAccount.id);
    } else {
      // if account is not selected, add it to the projection
      tempProjection.accounts.push(tempAccount);
    }
    setCurrentProjection(tempProjection);
  };

  // Selected indicator
  const SelectedIndicator = ({ isSelected }) => {
    // if isSelected, return a green circle with a checkmark inside. if not return a grey circle with a plus inside
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isSelected ? (
          <div style={{ height: "20px", width: "20px", borderRadius: "50%", backgroundColor: "#5CC958", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Check size={16} color={"#FFFFFF"} />
          </div>
        ) : (
          <div style={{ height: "20px", width: "20px", borderRadius: "50%", backgroundColor: "#EDEDED", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Plus size={16} color={"#0C0E29"} />
          </div>
        )}
      </div>
    );
  };

  const toggleRevenueCollapsed = () => {
    setRevenueCollapsed((prev) => !prev);
  };

  const toggleCostsCollapsed = () => {
    setCostsCollapsed((prev) => !prev);
  };

  const setAccountProjectionPercentage = (accountId, value) => {
    let tempProjection = { ...currentProjection };
    let tempAccount = tempProjection.accounts.find((account) => account.id === accountId);
    if (tempAccount) {
      tempAccount.percentage = value;
    }
    setCurrentProjection(tempProjection);
  };

  const handleApplyProjection = () => {
    handleSetActiveProjection(JSON.parse(JSON.stringify(currentProjection))); // Use deep copy to avoid reference issues
    setAppliedProjection(JSON.parse(JSON.stringify(currentProjection))); // Use deep copy to avoid reference issues
  };

  const handleResetProjection = () => {
    // reset currentProjection and appliedProjection to null
    setCurrentProjection(null);
    setAppliedProjection(null);
    handleSetActiveProjection(null); // Reset the active projection in the parent component
    setPriceDefinitionSearchValue("");
  };

  const didAccountChange = (accountId) => {
    // Check if the accountId is in the appliedProjection and not in the currentProjection
    const accountInAppliedProjection = appliedProjection?.accounts?.find((account) => account.id === accountId);
    const accountInCurrentProjection = currentProjection?.accounts?.find((account) => account.id === accountId);

    if (!accountInAppliedProjection || !accountInCurrentProjection) {
      return false; // If either account is not found, return false
    }

    return accountInAppliedProjection.percentage !== accountInCurrentProjection.percentage;
  };

  // Price Definition handlers
  const handlePriceDefinitionSelection = (priceDef) => {
    let tempProjection = currentProjection ? { ...currentProjection } : { accounts: [], priceDefinitions: [] };
    if (!tempProjection.priceDefinitions) {
      tempProjection.priceDefinitions = [];
    }

    const existingIndex = tempProjection.priceDefinitions.findIndex(
      (pd) => pd.priceDefinitionId === priceDef.priceDefinitionId
    );

    if (existingIndex >= 0) {
      tempProjection.priceDefinitions = tempProjection.priceDefinitions.filter((_, i) => i !== existingIndex);
    } else {
      tempProjection.priceDefinitions.push({
        priceDefinitionId: priceDef.priceDefinitionId,
        priceDefinitionName: priceDef.priceDefinitionName,
        currentPrice: priceDef.price,
        newPrice: priceDef.price,
        percentage: 100,
      });
    }
    setCurrentProjection(tempProjection);
  };

  const setPriceDefinitionNewPrice = (priceDefId, value) => {
    let tempProjection = { ...currentProjection };
    let pd = tempProjection.priceDefinitions.find(
      (p) => p.priceDefinitionId === priceDefId
    );
    if (pd) {
      pd.newPrice = value === "" ? "" : Number(value);
      pd.percentage = pd.currentPrice > 0 ? Math.round(((value === "" ? 0 : Number(value)) / pd.currentPrice) * 100 * 100) / 100 : 0;
    }
    setCurrentProjection(tempProjection);
  };

  const setPriceDefinitionPercentage = (priceDefId, value) => {
    let tempProjection = { ...currentProjection };
    let pd = tempProjection.priceDefinitions.find(
      (p) => p.priceDefinitionId === priceDefId
    );
    if (pd) {
      pd.percentage = value === "" ? "" : Number(value);
      pd.newPrice = Math.round(pd.currentPrice * ((value === "" ? 0 : Number(value)) / 100) * 100) / 100;
    }
    setCurrentProjection(tempProjection);
  };

  const didPriceDefinitionChange = (priceDefId) => {
    const pdInApplied = appliedProjection?.priceDefinitions?.find(
      (p) => p.priceDefinitionId === priceDefId
    );
    const pdInCurrent = currentProjection?.priceDefinitions?.find(
      (p) => p.priceDefinitionId === priceDefId
    );
    if (!pdInApplied || !pdInCurrent) return false;
    return pdInApplied.newPrice !== pdInCurrent.newPrice;
  };

  const isPriceDefinitionSelected = (priceDefId) => {
    return currentProjection?.priceDefinitions?.some(
      (pd) => pd.priceDefinitionId === priceDefId
    );
  };

  // Mutual exclusivity: when applying, only send the active tab's data
  const handleApplyProjectionForTab = () => {
    setIsApplying(true);
    let projectionToApply;
    if (selectedTab === "prices") {
      projectionToApply = { accounts: currentProjection?.accounts || [], priceDefinitions: [] };
    } else {
      projectionToApply = { accounts: [], priceDefinitions: currentProjection?.priceDefinitions || [] };
    }
    setTimeout(() => {
      handleSetActiveProjection(JSON.parse(JSON.stringify(projectionToApply)));
      setAppliedProjection(JSON.parse(JSON.stringify(projectionToApply)));
      setCurrentProjection(JSON.parse(JSON.stringify(projectionToApply)));
      setIsApplying(false);
    }, 0);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }} ref={buttonRef}>
      <button className={`${classes.button} ${isOpen ? classes.buttonOpen : ""}`} onClick={toggleDropdown}>
        Projections
        <TrendingUpDown size={16} color={"#0C0E29"} />
      </button>
      <div
        className={`${classes.dropdown} ${isOpen ? classes.dropdownOpen : classes.dropdownClosed} ${mobileAlignmentClass}`}
        // style={{ left: mobileAlignment === "right" ? "auto" : null, right: mobileAlignment === "right" ? 0 : null, transform: mobileAlignment !== null ? "translateX(0)" : null }}
      >
        <TabSelector value={selectedTab} onChange={handleGraphTypeSelection} fullwidth style={{ backgroundColor: "#EDEDED", marginBottom: "16px" }}>
          <Tab value="priceDefinitions">Price definitions</Tab>
          <Tab value="prices">Accounts</Tab>
        </TabSelector>
        {/* Tab contents */}
        {/* Prices */}
        {selectedTab === "prices" && (
          <div className={classes.tabContent}>
            <div className={classes.addAccountContainer}>
              <Label>Add an Account</Label>
              <div ref={openDropdownButtonRef} className={`${classes.selectTriggerInput}`} onClick={() => setAccountDropdownOpen(true)}>
                <input
                  type="text"
                  style={{ width: "100%" }}
                  value={accountSearchValue}
                  onChange={(e) => setAccountSearchValue(e.target.value)}
                  onFocus={() => setAccountDropdownOpen(true)}
                  className={classes.selectTriggerInputField}
                  placeholder="Account Name"
                />
                <button
                  type="button"
                  className={classes.selectTriggerChevronButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAccountDropdownOpen(!accountDropdownOpen);
                  }}
                >
                  <ChevronDown className={`${classes.selectTriggerIcon} ${accountDropdownOpen ? classes.selectTriggerIconOpen : ""}`} />
                </button>
              </div>
              {accountDropdownOpen &&
                ReactDOM.createPortal(
                  <div ref={dropdownRef} className={classes.dropdownContainer} style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownWidth }}>
                    {allAccounts.filter((account) => account.name.toLowerCase().includes(accountSearchValue.toLowerCase())).length > 0 ? (
                      allAccounts
                        .filter((account) => account.name.toLowerCase().includes(accountSearchValue.toLowerCase()))
                        .map((account, index) => (
                          <button className={classes.dropdown__menuItem} onClick={() => handleAccountSelection(account)} key={"Type_" + index}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {account.name} - {account.companyName}
                              {account.isRevenue ? (
                                <div style={{ height: "6px", width: "6px", borderRadius: "50%", backgroundColor: "#5CC958" }} />
                              ) : (
                                <div style={{ height: "6px", width: "6px", borderRadius: "50%", backgroundColor: "#D84949" }} />
                              )}
                            </div>
                            {/* selected indicator. Check if account is included in currentProjection */}
                            <SelectedIndicator isSelected={currentProjection?.accounts?.some((a) => a.id === account.id)} />
                          </button>
                        ))
                    ) : (
                      <div className={classes.dropdown__emptyState}>
                        <CircleOff className={classes.emptyState__icon} />
                        <p className={classes.emptyState__text}>There are no accounts to select</p>
                      </div>
                    )}
                  </div>,
                  document.body
                )}
            </div>
            {/* Show selected accounts */}

            <div className={classes.currentProjectionContainer}>
              {/* Revenue */}
              {currentProjection?.accounts?.filter((account) => account.isRevenue).length > 0 && (
                <div className={classes.currentProjection__category}>
                  <div className={classes.currentProjection__categoryHeader} onClick={toggleRevenueCollapsed}>
                    <div className={classes.currentProjection__categoryTitleContainer}>
                      <Chip variant="success">Revenue</Chip>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <p className={classes.currentProjection__categoryCount}>
                        {currentProjection?.accounts?.filter((account) => account.isRevenue).length} Simulation
                        {currentProjection?.accounts?.filter((account) => account.isRevenue).length > 1 ? "s" : ""}
                      </p>
                      <button className={classes.currentProjection__toggleButton}>
                        <ChevronDown
                          size={20}
                          strokeWidth={1.4}
                          color="#0C0E29"
                          style={{
                            transform: revenueCollapsed ? "rotate(0deg)" : "rotate(-180deg)",
                            transition: "transform 0.3s ease",
                          }}
                        />
                      </button>
                    </div>
                  </div>
                  {!revenueCollapsed && (
                    <div className={classes.currentProjection__categoryUsage}>
                      <div className={classes.dividerLine} />
                      <Divider style={{ margin: "1rem 0" }} />
                      {currentProjection?.accounts?.filter((account) => account.isRevenue).length > 0 && (
                        <div className={classes.currentProjection__accounts}>
                          {currentProjection?.accounts
                            .filter((account) => account.isRevenue) // Filter companies with non-empty activeDateRanges
                            .sort((a, b) => a.name.localeCompare(b.name)) // Sort by name
                            .map((account) => {
                              return (
                                <div key={account.id} className={classes.accounts__accountContainer}>
                                  <p className={classes.accounts__accountName}>{account.name}</p>
                                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                    {didAccountChange(account.id) && (
                                      <Chip variant="warning" size="extraSmall">
                                        Modified
                                      </Chip>
                                    )}
                                    <Input
                                      type="number"
                                      value={account.percentage}
                                      style={{ width: "100px", color: account.percentage > 100 ? "#3AB037" : account.percentage == 100 ? "#0C0E29" : "#D84949" }}
                                      onChange={(e) => setAccountProjectionPercentage(account.id, e.target.value)}
                                      endAdornment={<Percent />}
                                      hideControls
                                      min={0}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* Costs */}
              {currentProjection?.accounts?.filter((account) => !account.isRevenue).length > 0 && (
                <div className={classes.currentProjection__category}>
                  <div className={classes.currentProjection__categoryHeader} onClick={toggleCostsCollapsed}>
                    <div className={classes.currentProjection__categoryTitleContainer}>
                      <Chip variant="danger">Costs</Chip>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <p className={classes.currentProjection__categoryCount}>
                        {currentProjection?.accounts?.filter((account) => !account.isRevenue).length} Simulation
                        {currentProjection?.accounts?.filter((account) => !account.isRevenue).length > 1 ? "s" : ""}
                      </p>
                      <button className={classes.currentProjection__toggleButton}>
                        <ChevronDown
                          size={20}
                          strokeWidth={1.4}
                          color="#0C0E29"
                          style={{
                            transform: costsCollapsed ? "rotate(0deg)" : "rotate(-180deg)",
                            transition: "transform 0.3s ease",
                          }}
                        />
                      </button>
                    </div>
                  </div>
                  {!costsCollapsed && (
                    <div className={classes.currentProjection__categoryUsage}>
                      <div className={classes.dividerLine} />
                      <Divider style={{ margin: "1rem 0" }} />
                      {currentProjection?.accounts?.filter((account) => !account.isRevenue).length > 0 && (
                        <div className={classes.currentProjection__accounts}>
                          {currentProjection?.accounts
                            .filter((account) => !account.isRevenue) // Filter companies with non-empty activeDateRanges
                            .sort((a, b) => a.name.localeCompare(b.name)) // Sort by name
                            .map((account) => {
                              return (
                                <div key={account.id} className={classes.accounts__accountContainer}>
                                  <p className={classes.accounts__accountName}>{account.name}</p>
                                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                    {didAccountChange(account.id) && (
                                      <Chip variant="warning" size="extraSmall">
                                        Modified
                                      </Chip>
                                    )}
                                    <Input
                                      type="number"
                                      value={account.percentage}
                                      style={{ width: "100px", color: account.percentage > 100 ? "#D84949" : account.percentage == 100 ? "#0C0E29" : "#3AB037" }}
                                      onChange={(e) => setAccountProjectionPercentage(account.id, e.target.value)}
                                      endAdornment={<Percent />}
                                      hideControls
                                      min={0}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {!currentProjection?.accounts?.length > 0 && (
              <div className={classes.emptyStateContainer}>
                <CircleOff className={classes.emptyState__icon} />
                <p className={classes.emptyState__text}>There are no accounts selected for projection</p>
              </div>
            )}
            {currentProjection?.accounts?.length > 0 && (
              <div style={{ display: "flex", flexDirection: "row", gap: ".5rem", marginTop: "1rem" }}>
                <Button onClick={handleResetProjection} variant={"outlined"} iconButton startIcon={<RotateCcw />} />
                <Button onClick={handleApplyProjectionForTab} fullWidth loading={isApplying}>
                  Apply projection
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Price definitions */}
        {selectedTab === "priceDefinitions" && (
          <div className={classes.tabContent}>
            <div className={classes.addAccountContainer}>
              <Label>Add a Price Definition</Label>
              <div ref={openPdDropdownButtonRef} className={`${classes.selectTriggerInput}`} onClick={() => setPriceDefinitionDropdownOpen(true)}>
                <input
                  type="text"
                  style={{ width: "100%" }}
                  value={priceDefinitionSearchValue}
                  onChange={(e) => setPriceDefinitionSearchValue(e.target.value)}
                  onFocus={() => setPriceDefinitionDropdownOpen(true)}
                  className={classes.selectTriggerInputField}
                  placeholder="Price Definition Name"
                />
                <button
                  type="button"
                  className={classes.selectTriggerChevronButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPriceDefinitionDropdownOpen(!priceDefinitionDropdownOpen);
                  }}
                >
                  <ChevronDown className={`${classes.selectTriggerIcon} ${priceDefinitionDropdownOpen ? classes.selectTriggerIconOpen : ""}`} />
                </button>
              </div>
              {priceDefinitionDropdownOpen &&
                ReactDOM.createPortal(
                  <div ref={pdDropdownRef} className={classes.dropdownContainer} style={{ top: pdDropdownPosition.top, left: pdDropdownPosition.left, width: pdDropdownWidth }}>
                    {allPriceDefinitions.filter((pd) => pd.priceDefinitionName.toLowerCase().includes(priceDefinitionSearchValue.toLowerCase())).length > 0 ? (
                      allPriceDefinitions
                        .filter((pd) => pd.priceDefinitionName.toLowerCase().includes(priceDefinitionSearchValue.toLowerCase()))
                        .map((pd, index) => (
                          <button
                            className={classes.dropdown__menuItem}
                            onClick={() => handlePriceDefinitionSelection(pd)}
                            key={`PD_${pd.priceDefinitionId}_${index}`}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {pd.priceDefinitionName}
                            </div>
                            <SelectedIndicator isSelected={isPriceDefinitionSelected(pd.priceDefinitionId)} />
                          </button>
                        ))
                    ) : (
                      <div className={classes.dropdown__emptyState}>
                        <CircleOff className={classes.emptyState__icon} />
                        <p className={classes.emptyState__text}>There are no price definitions to select</p>
                      </div>
                    )}
                  </div>,
                  document.body
                )}
            </div>
            {/* Show selected price definitions */}
            <div className={classes.currentProjectionContainer}>
              {currentProjection?.priceDefinitions?.length > 0 && (
                <div className={classes.currentProjection__category}>
                  <div className={classes.currentProjection__categoryUsage}>
                    <div className={classes.currentProjection__accounts}>
                      {currentProjection.priceDefinitions
                        .sort((a, b) => a.priceDefinitionName.localeCompare(b.priceDefinitionName))
                        .map((pd) => {
                          const priceColor = pd.percentage > 100 ? "#D84949" : pd.percentage < 100 ? "#3AB037" : "#0C0E29";
                          const percentageColor = pd.percentage > 100 ? "#D84949" : pd.percentage < 100 ? "#3AB037" : "#0C0E29";
                          const currencySymbol = currency?.symbol || "€";
                          return (
                            <div key={pd.priceDefinitionId} className={classes.priceDefinition__container}>
                              <div className={classes.priceDefinition__header}>
                                <p className={classes.accounts__accountName}>{pd.priceDefinitionName}</p>
                              </div>
                              <div className={classes.priceDefinition__inputs}>
                                <div className={classes.priceDefinition__inputGroup}>
                                  <span className={classes.priceDefinition__inputLabel}>Current</span>
                                  <Input
                                    type="number"
                                    name={`currentPrice_${pd.priceDefinitionId}`}
                                    value={pd.currentPrice}
                                    style={{ width: "100px" }}
                                    disabled
                                    onChange={() => {}}
                                    startAdornment={<span style={{ fontSize: "14px" }}>{currencySymbol}</span>}
                                    hideControls
                                  />
                                </div>
                                <div className={classes.priceDefinition__inputGroup}>
                                  <span className={classes.priceDefinition__inputLabel}>New</span>
                                  <Input
                                    type="number"
                                    value={pd.newPrice}
                                    style={{ width: "100px", color: priceColor }}
                                    onChange={(e) => setPriceDefinitionNewPrice(pd.priceDefinitionId, e.target.value)}
                                    startAdornment={<span style={{ fontSize: "14px" }}>{currencySymbol}</span>}
                                    hideControls
                                    min={0}
                                  />
                                </div>
                                <div className={classes.priceDefinition__inputGroup}>
                                  <Input
                                    type="number"
                                    value={pd.percentage}
                                    style={{ width: "96px", color: percentageColor }}
                                    onChange={(e) => setPriceDefinitionPercentage(pd.priceDefinitionId, e.target.value)}
                                    endAdornment={<Percent />}
                                    hideControls
                                    min={0}
                                  />
                                </div>
                                {didPriceDefinitionChange(pd.priceDefinitionId) && (
                                  <Chip variant="warning" size="extraSmall">
                                    Modified
                                  </Chip>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!currentProjection?.priceDefinitions?.length > 0 && (
              <div className={classes.emptyStateContainer}>
                <CircleOff className={classes.emptyState__icon} />
                <p className={classes.emptyState__text}>There are no price definitions selected for projection</p>
              </div>
            )}
            {currentProjection?.priceDefinitions?.length > 0 && (
              <div style={{ display: "flex", flexDirection: "row", gap: ".5rem", marginTop: "1rem" }}>
                <Button onClick={handleResetProjection} variant={"outlined"} iconButton startIcon={<RotateCcw />} />
                <Button onClick={handleApplyProjectionForTab} fullWidth loading={isApplying}>
                  Apply projection
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  button: {
    height: "32px",
    padding: "4px 8px 4px 12px",
    backgroundColor: "#DCDCDC",
    color: "#0C0E29",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#BDBDBD",
    },
  },

  buttonOpen: {
    backgroundColor: "#FFFFFF", // Change background color to white when dropdown is open

    "&:hover": {
      backgroundColor: "#FFFFFF", // Keep the background color white on hover when open
    },
  },

  dropdown: {
    position: "absolute",
    top: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    marginTop: "4px",
    width: "400px",
    maxWidth: "calc(90vw)",
    padding: "16px",
    backgroundColor: "#fff",
    border: "1px solid #DCDCDC",
    borderRadius: "8px",
    boxShadow: "0 4px 4px -4px rgba(12, 12, 13, 0.05), 0 6px 32px -4px rgba(12, 12, 13, 0.1)",
    zIndex: 9998,
    opacity: 0,
    transformOrigin: "top center",
    transition: "opacity 0.3s ease, transform 0.3s ease",
  },

  dropdownOpen: {
    opacity: 1,
    transform: "translateX(-50%)",
    pointerEvents: "auto",
  },

  dropdownClosed: {
    opacity: 0,
    transform: "translateX(-50%)",
    pointerEvents: "none",
  },

  // SelectTrigger-like input styles
  selectTriggerInput: {
    padding: "8px 12px",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    backgroundColor: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "border-color 0.2s",
    "&:hover": {
      borderColor: "#888",
    },
  },

  selectTriggerInputError: {
    borderColor: "#D84949 !important",
    "&:hover": {
      borderColor: "#D84949 !important",
    },
  },

  selectTriggerInputField: {
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    flex: 1,
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    cursor: "text",
    color: "#0C0E29",
    "&::placeholder": {
      color: "rgba(12, 14, 41, 0.6)",
    },
  },

  selectTriggerChevronButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translateX(2px)",
    "&:hover": {
      backgroundColor: "#F0F0F0",
    },
  },

  selectTriggerIcon: {
    width: "16px",
    height: "16px",
    transition: "transform 0.2s",
    cursor: "pointer",
    color: "#666",
  },

  selectTriggerIconOpen: {
    transform: "rotate(180deg)",
  },

  dropdownContainer: {
    width: "100%",
    minWidth: "160px",
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    zIndex: 9998,
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",

    display: "flex",
    flexDirection: "column",

    padding: "4px",

    "&::-webkit-scrollbar": {
      width: "4px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  dropdown__menuItem: {
    width: "100%",
    minHeight: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F4F4F4",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__emptyState: {
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",

    padding: "24px 0 16px 0",
  },

  emptyStateContainer: {
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",

    padding: "24px 0 16px 0",
    marginTop: "16px",
  },

  emptyState__icon: {
    width: "24px",
    height: "24px",
    color: "#0C0E29",

    strokeWidth: "1.4px",
  },

  emptyState__text: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    margin: "0",
  },

  currentProjectionContainer: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "8px",

    marginTop: "16px",
  },

  currentProjection__category: {
    width: "100%",

    padding: "16px",
    border: "1px solid #DCDCDC",
    borderRadius: "8px",
  },

  currentProjection__categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  currentProjection__categoryTitleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  currentProjection__categoryTitle: {
    height: "24px",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",

    borderRadius: "4px",

    padding: "0px 12px",
    verticalAlign: "middle",
  },

  currentProjection__categoryCount: {
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  currentProjection__toggleButton: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "0px",
    margin: "0px",
    height: "24px",
    width: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "4px",
    transition: "all ease-in-out 0.2s",
    "&:hover": {
      backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  currentProjection__emptyStateContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  currentProjection__accounts: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: ".5rem",

    // max height of 200px and scrollable
    maxHeight: "12.5rem",
    overflowY: "auto",
    overflowX: "hidden",

    // custom scrollbar
    "&::-webkit-scrollbar": {
      width: "4px",
      paddingLeft: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  accounts__accountContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },

  accounts__accountName: {
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  // mobile alignment
  leftMobileAlignment: {
    left: "0",
    transform: "translateX(0)",
  },
  centerMobileAlignment: {
    left: "50%",
    transform: "translateX(-50%)",
  },
  rightMobileAlignment: {
    left: "auto",
    right: "0",
    transform: "translateX(0)",
  },

  // Price definition styles
  priceDefinition__container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    paddingBottom: "8px",
    borderBottom: "1px solid #F4F4F4",
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: 0,
    },
  },

  priceDefinition__header: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  priceDefinition__inputs: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    gap: "8px",
    flexWrap: "wrap",
  },

  priceDefinition__inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  priceDefinition__inputLabel: {
    fontSize: "11px",
    fontFamily: "Lato, sans-serif",
    color: "#888",
  },
});

export default ProjectionsDialog;
